/**
 * Client-side hardening for legacy/Direct fetch():
 *  - Prefix relative URLs with VITE_API_BASE (default '/v1'), with safety guards
 *  - Avoid double '/v1' when base already ends with '/v1'
 *  - Add X-Tenant-ID header if missing
 *  - For unsafe methods (POST/PUT/PATCH/DELETE): ensure CSRF cookie via /v1/csrf,
 *    include X-CSRF-Token header and credentials: 'include'
 *  - Add a simple traceparent header (dev-friendly); full trace is in '@/client'
 *  - Record last request (url/status/trace-id) in sessionStorage for diagnostics
 *
 * Only runs in the browser.
 */

import './realtimePrefix'

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>
const SAFE = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE'])
const DEV  = typeof window !== 'undefined' && (import.meta as any)?.env?.MODE === 'development'

/* -------------------- Base computation (safe) -------------------- */
const RAW_BASE = (import.meta as any)?.env?.VITE_API_BASE as string | undefined

function computeBase(): string {
  if (RAW_BASE && /^https?:\/\//i.test(RAW_BASE)) {
    return RAW_BASE.replace(/\/$/, '')
  }
  if (RAW_BASE && RAW_BASE.trim().startsWith('/')) {
    const cleaned = RAW_BASE.trim()
    // Prevent '/' collapsing to '' which would bypass /v1 and hit the SPA
    if (cleaned === '/') return '/v1'
    return cleaned.replace(/\/$/, '')
  }
  return '/v1'
}
const API_BASE = computeBase()

/* ---------------- URL join with /v1 double-prefix guard --------- */
function joinWithBase(path: string): string {
  if (/^https?:\/\//i.test(path)) return path
  // normalize leading slash
  let p = path.startsWith('/') ? path : `/${path}`
  // SAFETY: if base ends with /v1 and path starts with /v1/, strip duplicate
  if (API_BASE.endsWith('/v1') && p.startsWith('/v1/')) {
    p = p.slice(3) // remove leading '/v1'
    if (DEV) console.warn('[client.extra] stripped duplicate /v1 from path', { original: path, normalized: p })
  }
  return `${API_BASE}${p}`
}

/* -------------------------- CSRF helpers ------------------------ */
function getCookie(name: string) {
  const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'))
  return m ? decodeURIComponent(m[1]) : undefined
}

async function ensureCsrf() {
  if (!getCookie('csrftoken')) {
    // Same-origin path so Vite/NGINX proxy sets cookie
    await fetch('/v1/csrf', { credentials: 'include' }).catch(() => {})
  }
}

/* ----------------------- Trace header (lite) -------------------- */
function traceparent(): string {
  try {
    const rand = Math.random().toString(16).slice(2).padEnd(32, '0')
    return `00-${rand}-${rand.slice(0, 16)}-01`
  } catch {
    return '00-0'.padEnd(55, '0')
  }
}

/* ------------- Build Request from input/init safely ------------- */
function buildRequest(input: RequestInfo | URL, init?: RequestInit): Request {
  // Derive original method/headers/credentials/body
  let method = (init?.method || 'GET').toUpperCase()
  let headers = new Headers(init?.headers || {})
  let credentials = init?.credentials
  let mode       = init?.mode
  let cache      = init?.cache
  let redirect   = init?.redirect
  let referrer   = init?.referrer
  let refPol     = init?.referrerPolicy
  let integrity  = init?.integrity
  let keepalive  = (init as any)?.keepalive
  let signal     = init?.signal
  let body: BodyInit | null | undefined = init?.body ?? undefined

  let urlStr: string

  if (typeof input === 'string') {
    urlStr = input
  } else if (input instanceof URL) {
    urlStr = input.toString()
  } else {
    // Request
    const req = input as Request
    urlStr    = req.url
    method    = (init?.method || req.method || 'GET').toUpperCase()
    credentials = init?.credentials ?? req.credentials
    mode        = init?.mode        ?? req.mode
    cache       = init?.cache       ?? req.cache
    redirect    = init?.redirect    ?? req.redirect
    referrer    = init?.referrer    ?? req.referrer
    refPol      = init?.referrerPolicy ?? (req.referrerPolicy as any)
    integrity   = init?.integrity   ?? req.integrity
    keepalive   = (init as any)?.keepalive ?? (req as any)?.keepalive
    signal      = init?.signal      ?? req.signal

    // Merge headers (init wins)
    const hdrs = new Headers(req.headers)
    headers.forEach((v, k) => hdrs.set(k, v))
    headers = hdrs

    // Preserve body (clone to avoid locking)
    if (init?.body !== undefined) {
      body = init.body
    } else {
      try {
        // @ts-ignore - TS types don’t expose Request.body init easily
        body = req.body ? (req.clone() as any).body : undefined
      } catch {
        body = undefined
      }
    }
  }

  // Resolve final URL
  let finalUrl: string

  // 1) Absolute URLs and already-canonical /v1/* should pass through unchanged.
  if (/^https?:\/\//i.test(urlStr) || urlStr.startsWith('/v1/')) {
    finalUrl = urlStr

  // 2) IMPORTANT: treat /api/* as "already base-prefixed".
  // This prevents accidental rewriting /api/v1/... -> /v1/api/v1/...
  // Some legacy modules (or embedded workflow composer UI) may still call /api/*.
  } else if (urlStr.startsWith('/api/')) {
    finalUrl = urlStr

  // 3) Other absolute-path requests get joined to API_BASE (usually /v1)
  } else if (urlStr.startsWith('/')) {
    finalUrl = joinWithBase(urlStr)

  // 4) Relative like 'foo/bar'
  } else {
    finalUrl = joinWithBase('/' + urlStr.replace(/^\/+/, ''))
  }

  // Resolve final URL
  //let finalUrl: string
  //if (/^https?:\/\//i.test(urlStr) || urlStr.startsWith('/v1/')) {
  //  finalUrl = urlStr
  //} else if (urlStr.startsWith('/')) {
  //  finalUrl = joinWithBase(urlStr)
  //} else {
    // relative like 'foo/bar' – normalize to /foo/bar then join
  //  finalUrl = joinWithBase('/' + urlStr.replace(/^\/+/, ''))
  //}

    // ---- Default headers: tenant + trace + (admin) auth ----

  // Read JWT that LoginPage/AuthProvider stores
  const token = (() => {
    try {
      return (window.localStorage?.getItem?.('orchestrator.jwt') || '').trim()
    } catch {
      return ''
    }
  })()

  // Detect admin API requests (both canonical and legacy proxied)
  // - /v1/admin/...     (canonical)
  // - /api/v1/admin/... (legacy proxy path used by some pages)
  const isAdminApi =
    finalUrl.includes('/v1/admin/') ||
    finalUrl.includes('/api/v1/admin/')

  // Attach Authorization for the backend surfaces that require the platform JWT:
  // - admin APIs
  // - /auth/me identity resolution after sign-in
  if (token && !headers.has('Authorization')) {
    try {
      const u = new URL(finalUrl, window.location.origin)
      const sameOrigin = u.origin === window.location.origin

      const shouldAttachAuth =
        u.pathname.startsWith('/v1/admin/') ||
        u.pathname.startsWith('/api/v1/admin/') ||
        u.pathname === '/v1/auth/me' ||
        u.pathname === '/api/v1/auth/me'

      if (sameOrigin && shouldAttachAuth) {
        headers.set('Authorization', `Bearer ${token}`)
      }
    } catch {
      // ignore URL parse errors
    }
  }

  // Tenant header: keep for non-admin compatibility, but DO NOT rely on it for admin
  if (!headers.has('X-Tenant-ID') && !isAdminApi) {
    headers.set('X-Tenant-ID', 'root')
  }

  if (!headers.has('traceparent')) headers.set('traceparent', traceparent())


  // Default headers: tenant + trace
//  if (!headers.has('X-Tenant-ID')) headers.set('X-Tenant-ID', 'root')
//  if (!headers.has('traceparent')) headers.set('traceparent', traceparent())

  // Unsafe methods → ensure CSRF token + include credentials
  if (!SAFE.has(method)) {
    // We only attempt CSRF for same-origin relative/backend calls
    const looksSameOrigin = !/^https?:\/\//i.test(finalUrl) || finalUrl.startsWith(window.location.origin)
    if (looksSameOrigin) {
      // Ensure token cookie exists
      // (ignore errors; middleware will still block if token missing)
      void ensureCsrf()
      const token = getCookie('csrftoken')
      if (token && !headers.has('X-CSRF-Token')) headers.set('X-CSRF-Token', token)
      if (credentials === undefined) credentials = 'include'
    }
  }

  return new Request(finalUrl, {
    method,
    headers,
    body,
    mode,
    credentials,
    cache,
    redirect,
    referrer,
    referrerPolicy: refPol,
    integrity,
    // @ts-ignore keepalive exists at runtime
    keepalive,
    signal,
  })
}

/* --------------------- record last request ---------------------- */
function recordLast(res: Response, url: string) {
  try {
    const traceId = res.headers.get('x-trace-id') || ''
    const info = {
      url,
      status: res.status,
      trace_id: traceId,
      date: new Date().toISOString(),
    }
    window.sessionStorage?.setItem?.('lastRequestInfo', JSON.stringify(info))
  } catch {
    /* ignore */
  }
}

/* ---------------------- Patch global fetch ---------------------- */
(function patchGlobalFetch() {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return
  const orig: FetchLike = window.fetch.bind(window)

  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    const req = buildRequest(input, init)
    const res = await orig(req)
    recordLast(res, req.url)
    return res
  }) as FetchLike

  // Optional legacy global
  ;(window as any).api = async (path: string, opts?: RequestInit) => {
    const req = buildRequest(path, opts)
    const res = await orig(req)
    recordLast(res, req.url)
    return res
  }
})()

// Debug (once, on load)
if (DEV) {
  // eslint-disable-next-line no-console
  console.log('[client.extra] API_BASE =', API_BASE, 'RAW =', RAW_BASE)
}